import { orderSelectors } from 'ducks/orderComponents'
import { orgSelectors } from 'ducks/orgs'
import type LineItemType from 'pages/ordering/OrderLineItem'
import { getProjectOrderAllocationKey, getResolvedSource } from 'pages/ordering/utils'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import restClient from 'restClient'

const restClientInstance = restClient(window.API_ROOT + '/api')

/** Subset of system fields returned when projects are fetched with `fields=...,systems` (e.g. cart supplier assist). */
export type ProjectInfoSystem = {
  uuid?: string
  name?: string | null
  module_quantity?: number
}

export type ProjectInfo = {
  id: number
  address: string
  business_name?: string
  installation_date?: string
  systems?: ProjectInfoSystem[]
}

export type LineItemWithResolvedSource = LineItemType & { resolvedSource: ReturnType<typeof getResolvedSource> }

export type ProjectLineItemGroup = {
  projectId: number
  systemId?: number
  projectInfo?: ProjectInfo
  lineItems: LineItemWithResolvedSource[]
  /** Optional title override (e.g. for a single combined table) */
  title?: string
  /** Map of project ID to project info (for Project Allocations column) */
  projectInfoMap?: Record<number, ProjectInfo>
}

/**
 * Hook to fetch and organize data for the Order By Project Cart page.
 * - Gets unconfirmed line items from Redux (pending from project selection)
 * - Gets confirmed line items (already in cart)
 * - Groups unconfirmed by project/system
 * - Fetches project addresses
 *
 * Note: Component/ordering data is already populated on all line items
 * (both project items and shop items) so no componentsv2 fetch is needed.
 */
export const useOrderByProjectCartData = () => {
  const orgId = useSelector(orgSelectors.getOrg)?.id
  const unconfirmedLineItems = useSelector(orderSelectors.getUnconfirmedLineItems)
  const confirmedLineItems = useSelector(orderSelectors.getConfirmedLineItems)

  const [projectInfoMap, setProjectInfoMap] = useState<Record<number, ProjectInfo>>({})
  const [isLoadingProjects, setIsLoadingProjects] = useState(false)

  // Use ref to track what we've already fetched to prevent infinite loops
  const fetchedProjectIdsRef = useRef<string>('')

  // Separate line items into project-associated and non-project items
  const { groupedLineItems, nonProjectLineItems } = useMemo(() => {
    const groups: Record<string, ProjectLineItemGroup> = {}
    const nonProjectItems: LineItemWithResolvedSource[] = []

    unconfirmedLineItems.forEach((lineItem) => {
      const enriched = {
        ...lineItem,
        resolvedSource: getResolvedSource(lineItem.selectedDistributor, lineItem.orderingData),
      } as LineItemWithResolvedSource
      const hasProjectOrder = lineItem.projectOrder && lineItem.projectOrder.length > 0

      if (hasProjectOrder) {
        lineItem.projectOrder?.forEach((projectOrder) => {
          const key = getProjectOrderAllocationKey(projectOrder)

          if (!groups[key]) {
            groups[key] = {
              projectId: projectOrder.projectId,
              systemId: projectOrder.systemId,
              projectInfo: projectInfoMap[projectOrder.projectId],
              lineItems: [],
            }
          }

          groups[key].lineItems.push(enriched)
        })
      } else {
        nonProjectItems.push(enriched)
      }
    })

    return {
      groupedLineItems: Object.values(groups) as ProjectLineItemGroup[],
      nonProjectLineItems: nonProjectItems,
    }
  }, [unconfirmedLineItems, projectInfoMap])

  const confirmedLineItemsWithResolvedSource = useMemo(
    () =>
      confirmedLineItems.map(
        (item) =>
          ({
            ...item,
            resolvedSource: getResolvedSource(item.selectedDistributor, item.orderingData),
          } as LineItemWithResolvedSource)
      ),
    [confirmedLineItems]
  )

  // Extract unique project IDs
  const projectIds = useMemo(() => {
    const ids = new Set<number>()
    unconfirmedLineItems.forEach((lineItem) => {
      lineItem.projectOrder?.forEach((po) => ids.add(po.projectId))
    })
    return Array.from(ids)
  }, [unconfirmedLineItems])

  // Serialized key to detect changes
  const projectIdsKey = projectIds.sort().join(',')

  // Fetch project info when project IDs change
  useEffect(() => {
    if (!orgId || projectIds.length === 0) return
    if (fetchedProjectIdsRef.current === projectIdsKey) return

    fetchedProjectIdsRef.current = projectIdsKey
    setIsLoadingProjects(true)

    restClientInstance('CUSTOM_GET', 'custom', {
      url: `orgs/${orgId}/projects/?fields=id,address,business_name&filter=${projectIds.join(',')}`,
    })
      .then(({ data }) => {
        const newProjectInfoMap: Record<number, ProjectInfo> = {}
        data?.forEach((project: ProjectInfo) => {
          newProjectInfoMap[project.id] = project
        })
        setProjectInfoMap(newProjectInfoMap)
      })
      .catch((error) => {
        console.error('Failed to fetch project info:', error)
      })
      .finally(() => {
        setIsLoadingProjects(false)
      })
  }, [orgId, projectIds, projectIdsKey])

  return {
    groupedLineItems,
    nonProjectLineItems,
    confirmedLineItems: confirmedLineItemsWithResolvedSource,
    projectInfoMap,
    isLoading: isLoadingProjects,
    hasLineItems: unconfirmedLineItems.length > 0 || confirmedLineItems.length > 0,
  }
}

export default useOrderByProjectCartData
